import { useState, type FormEvent } from "react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import type { Person, PersonInput, PersonStatus } from "../api/types";
import {
  Badge,
  Field,
  InlineError,
  PageHeader,
  Panel,
  QueryState,
} from "../components/ui";

export function PeoplePage() {
  const membership = useActiveTenant();
  const canEdit = can(membership.role, "people.edit");
  const people = useTenantQuery(["people"], (api, t) => api.listPeople(t));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | PersonStatus>("");

  const create = useTenantMutation<PersonInput>(
    (api, t, input) => api.createPerson(t, input),
    [["people"], ["audit-events"]],
  );
  const toggleStatus = useTenantMutation<Person>(
    (api, t, person) =>
      api.updatePerson(t, person.id, { status: person.status === "active" ? "inactive" : "active" }),
    [["people"], ["audit-events"]],
  );

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    create.mutate(
      {
        name: String(data.get("name") || ""),
        email: String(data.get("email") || "") || null,
        phone: String(data.get("phone") || "") || null,
      },
      { onSuccess: () => form.reset() },
    );
  }

  return (
    <>
      <PageHeader
        title="Pessoas"
        subtitle="Moradores, proprietários, funcionários e hóspedes. O vínculo com unidades é feito em Ocupações."
      />
      {canEdit && (
        <Panel title="Nova pessoa">
          <form onSubmit={submit} className="form-grid">
            <Field label="Nome" name="name" required />
            <Field label="E-mail" name="email" type="email" />
            <Field label="Telefone" name="phone" />
            <div className="form-actions">
              <button type="submit" disabled={create.isPending}>
                {create.isPending ? "Salvando…" : "Cadastrar"}
              </button>
            </div>
          </form>
          <InlineError error={create.error} />
        </Panel>
      )}
      <Panel title="Pessoas cadastradas">
        <div className="toolbar">
          <label>
            Buscar
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, e-mail ou telefone"
            />
          </label>
          <label>
            Situação
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "" | PersonStatus)}>
              <option value="">Todas</option>
              <option value="active">Ativas</option>
              <option value="inactive">Inativas</option>
            </select>
          </label>
        </div>
        <QueryState query={people} empty="Nenhuma pessoa cadastrada ainda.">
          {(items) => {
            const needle = search.toLowerCase();
            const filtered = items.filter(
              (person) =>
                (!statusFilter || person.status === statusFilter) &&
                (!needle ||
                  person.name.toLowerCase().includes(needle) ||
                  (person.email ?? "").toLowerCase().includes(needle) ||
                  (person.phone ?? "").toLowerCase().includes(needle)),
            );
            if (filtered.length === 0) return <p className="state state-empty">Nenhuma pessoa encontrada com os filtros atuais.</p>;
            return (
              <div className="table">
                {filtered.map((person) => (
                  <div className="table-row" key={person.id}>
                    <span>
                      <strong>{person.name}</strong>
                      <small>{[person.email, person.phone].filter(Boolean).join(" · ") || "Sem contato"}</small>
                    </span>
                    <Badge tone={person.status === "active" ? "ok" : "muted"}>
                      {person.status === "active" ? "Ativa" : "Inativa"}
                    </Badge>
                    {canEdit && (
                      <span className="row-actions">
                        <button
                          type="button"
                          className="secondary"
                          disabled={toggleStatus.isPending}
                          onClick={() => toggleStatus.mutate(person)}
                        >
                          {person.status === "active" ? "Inativar" : "Reativar"}
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          }}
        </QueryState>
        <InlineError error={toggleStatus.error} />
      </Panel>
    </>
  );
}
